import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { ProtectedRoute } from "@/components/ProtectedRoute";
import { RoleRoute } from "@/components/RoleRoute";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { HqLayout } from "@/components/layout/HqLayout";
import { OwnerLayout } from "@/components/layout/OwnerLayout";
import { StaffLayout } from "@/components/layout/StaffLayout";
import { StudentLayout } from "@/components/layout/StudentLayout";
import { Skeleton } from "@/components/ui/Skeleton";

// Shared (any authenticated role)
const Help = lazy(() => import("@/pages/Help"));
// Public — fully anonymous
const Directory = lazy(() => import("@/pages/Directory"));

import Login from "@/pages/auth/Login";
const NotFound = lazy(() => import("@/pages/NotFound"));
const Register = lazy(() => import("@/pages/auth/Register"));
const Invite = lazy(() => import("@/pages/auth/Invite"));
const ForgotPassword = lazy(() => import("@/pages/auth/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/auth/ResetPassword"));
const RegisterQR = lazy(() => import("@/pages/RegisterQR"));
const PrivacyPolicy = lazy(() => import("@/pages/legal/PrivacyPolicy"));
const TermsAndConditions = lazy(() => import("@/pages/legal/TermsAndConditions"));

// Onboarding / lifecycle (auth required, no role check)
const Onboarding = lazy(() => import("@/pages/onboarding/Onboarding"));
const OnboardingPending = lazy(
  () => import("@/pages/onboarding/OnboardingPending"),
);
const OnboardingRejected = lazy(
  () => import("@/pages/onboarding/OnboardingRejected"),
);
const Suspended = lazy(() => import("@/pages/Suspended"));

// Admin (lazy)
const AdminDashboard = lazy(() => import("@/pages/admin/Dashboard"));
const AdminStudents = lazy(() => import("@/pages/admin/Students"));
const AdminStudentDetail = lazy(() => import("@/pages/admin/StudentDetail"));
const AdminContracts = lazy(() => import("@/pages/admin/Contracts"));
const AdminRenewals = lazy(() => import("@/pages/admin/Renewals"));
const AdminTransport = lazy(() => import("@/pages/admin/Transport"));
const AdminMaintenance = lazy(() => import("@/pages/admin/Maintenance"));
const AdminInspections = lazy(() => import("@/pages/admin/Inspections"));
const AdminNsfasPayouts = lazy(() => import("@/pages/admin/NsfasPayouts"));
const AdminNsfasImport = lazy(() => import("@/pages/admin/NsfasImport"));
const AdminNsfasExport = lazy(() => import("@/pages/admin/NsfasExport"));
const AdminNsfasSubmission = lazy(() => import("@/pages/admin/NsfasSubmission"));
const AdminExpenses = lazy(() => import("@/pages/admin/Expenses"));
const AdminPayments = lazy(() => import("@/pages/admin/Payments"));
const AdminCalendar = lazy(() => import("@/pages/admin/Calendar"));
const AdminChat = lazy(() => import("@/pages/admin/Chat"));
const DriverManifest = lazy(() => import("@/pages/driver/Manifest"));
const AdminAnnouncements = lazy(() => import("@/pages/admin/Announcements"));
const AdminInvites = lazy(() => import("@/pages/admin/Invites"));
const AdminRooms = lazy(() => import("@/pages/admin/Rooms"));
const AdminSettings = lazy(() => import("@/pages/admin/Settings"));
const AdminApprovals = lazy(() => import("@/pages/admin/Approvals"));
const AdminContractGenerator = lazy(
  () => import("@/pages/admin/ContractGenerator"),
);
const AdminContractSign = lazy(() => import("@/pages/admin/ContractSign"));

// HQ (super_admin)
const HqDashboard      = lazy(() => import("@/pages/hq/Dashboard"));
const HqCommunications = lazy(() => import("@/pages/hq/Communications"));
const HqProvision      = lazy(() => import("@/pages/hq/Provision"));
const HqCompanyDetail  = lazy(() => import("@/pages/hq/CompanyDetail"));
const HqActivityFeed   = lazy(() => import("@/pages/hq/ActivityFeed"));

// Owner
const OwnerDashboard = lazy(() => import("@/pages/owner/Dashboard"));
const OwnerProperties = lazy(() => import("@/pages/owner/Properties"));
const OwnerPropertyDetail = lazy(() => import("@/pages/owner/PropertyDetail"));
const OwnerUsers = lazy(() => import("@/pages/owner/Users"));
const OwnerTeamPermissions = lazy(() => import("@/pages/owner/TeamPermissions"));
const OwnerSettings        = lazy(() => import("@/pages/owner/Settings"));
const OwnerExpenses        = lazy(() => import("@/pages/owner/Expenses"));

// Staff
const StaffHome = lazy(() => import("@/pages/staff/Home"));
const StaffMaintenance = lazy(() => import("@/pages/staff/MaintenanceTickets"));
const StaffInspections = lazy(() => import("@/pages/staff/Inspections"));
const StaffExpenses = lazy(() => import("@/pages/staff/Expenses"));

// Student
const StudentHome = lazy(() => import("@/pages/student/Home"));
const StudentTransport = lazy(() => import("@/pages/student/Transport"));
const StudentMaintenance = lazy(() => import("@/pages/student/Maintenance"));
const StudentChat = lazy(() => import("@/pages/student/Chat"));
const StudentContract = lazy(() => import("@/pages/student/MyContract"));
const StudentProfile = lazy(() => import("@/pages/student/Profile"));
const StudentNotifications = lazy(() => import("@/pages/student/Notifications"));
const StudentMoveOut   = lazy(() => import("@/pages/student/MoveOut"));
const StudentDocuments = lazy(() => import("@/pages/student/Documents"));

function PageFallback() {
  return (
    <div className="p-8">
      <Skeleton className="h-8 w-48 mb-6" />
      <Skeleton className="h-32 w-full mb-4" />
      <Skeleton className="h-32 w-full" />
    </div>
  );
}

/** Wraps a lazy page so only the content area shows a skeleton,
 *  not the entire screen — prevents black flash on navigation. */
function Page({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageFallback />}>{children}</Suspense>;
}

export default function App() {
  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/driver/:propertyId" element={<Page><DriverManifest /></Page>} />
        <Route path="/invite" element={<Page><Invite /></Page>} />
        <Route path="/register-qr" element={<RegisterQR />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/privacy" element={<Page><PrivacyPolicy /></Page>} />
        <Route path="/terms" element={<Page><TermsAndConditions /></Page>} />
        <Route path="/directory" element={<Page><Directory /></Page>} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* Lifecycle routes — auth required, no role gate
            (ProtectedRoute redirects users here based on profile.status) */}
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute>
              <Onboarding />
            </ProtectedRoute>
          }
        />
        <Route
          path="/onboarding/pending"
          element={
            <ProtectedRoute>
              <OnboardingPending />
            </ProtectedRoute>
          }
        />
        <Route
          path="/onboarding/rejected"
          element={
            <ProtectedRoute>
              <OnboardingRejected />
            </ProtectedRoute>
          }
        />
        <Route
          path="/suspended"
          element={
            <ProtectedRoute>
              <Suspended />
            </ProtectedRoute>
          }
        />

        <Route
          path="/hq/*"
          element={
            <ProtectedRoute>
              <RoleRoute allowedRoles={["super_admin"]}>
                <HqLayout />
              </RoleRoute>
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard"         element={<Page><HqDashboard /></Page>} />
          <Route path="communications"    element={<Page><HqCommunications /></Page>} />
          <Route path="provision"         element={<Page><HqProvision /></Page>} />
          <Route path="company/:companyId" element={<Page><HqCompanyDetail /></Page>} />
          <Route path="activity"          element={<Page><HqActivityFeed /></Page>} />
        </Route>

        <Route
          path="/owner/*"
          element={
            <ProtectedRoute>
              <RoleRoute allowedRoles={["owner"]}>
                <OwnerLayout />
              </RoleRoute>
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<Page><OwnerDashboard /></Page>} />
          <Route path="properties" element={<Page><OwnerProperties /></Page>} />
          <Route path="properties/:id" element={<Page><OwnerPropertyDetail /></Page>} />
          <Route path="chat" element={<Page><AdminChat /></Page>} />
          <Route path="users" element={<Page><OwnerUsers /></Page>} />
          <Route path="team-permissions" element={<Page><OwnerTeamPermissions /></Page>} />
          <Route path="expenses" element={<Page><OwnerExpenses /></Page>} />
          <Route path="settings" element={<Page><OwnerSettings /></Page>} />
        </Route>

        <Route
          path="/admin/*"
          element={
            <ProtectedRoute>
              <RoleRoute allowedRoles={["admin", "owner"]}>
                <AdminLayout />
              </RoleRoute>
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<RoleRoute allowedRoles={["admin", "owner"]}><Page><AdminDashboard /></Page></RoleRoute>} />
          <Route path="students" element={<Page><AdminStudents /></Page>} />
          <Route path="students/:id" element={<Page><AdminStudentDetail /></Page>} />
          <Route path="contracts" element={<RoleRoute allowedRoles={["admin", "owner"]}><Page><AdminContracts /></Page></RoleRoute>} />
          <Route
            path="contracts/generate/:studentId"
            element={<RoleRoute allowedRoles={["admin", "owner"]}><Page><AdminContractGenerator /></Page></RoleRoute>}
          />
          <Route
            path="contracts/:id/sign"
            element={<RoleRoute allowedRoles={["admin", "owner"]}><Page><AdminContractSign /></Page></RoleRoute>}
          />
          <Route path="renewals" element={<RoleRoute allowedRoles={["admin", "owner"]}><Page><AdminRenewals /></Page></RoleRoute>} />
          <Route path="transport" element={<Page><AdminTransport /></Page>} />
          <Route path="maintenance" element={<Page><AdminMaintenance /></Page>} />
          <Route path="inspections" element={<Page><AdminInspections /></Page>} />
          <Route path="nsfas/payouts" element={<RoleRoute allowedRoles={["admin", "owner"]}><Page><AdminNsfasPayouts /></Page></RoleRoute>} />
          <Route path="nsfas/import" element={<RoleRoute allowedRoles={["admin", "owner"]}><Page><AdminNsfasImport /></Page></RoleRoute>} />
          <Route path="nsfas/export" element={<RoleRoute allowedRoles={["admin", "owner"]}><Page><AdminNsfasExport /></Page></RoleRoute>} />
          <Route path="nsfas/submission" element={<RoleRoute allowedRoles={["admin", "owner"]}><Page><AdminNsfasSubmission /></Page></RoleRoute>} />
          <Route path="expenses" element={<Page><AdminExpenses /></Page>} />
          <Route path="payments" element={<RoleRoute allowedRoles={["admin", "owner"]}><Page><AdminPayments /></Page></RoleRoute>} />
          <Route path="calendar" element={<Page><AdminCalendar /></Page>} />
          <Route path="chat" element={<Page><AdminChat /></Page>} />
          <Route path="users" element={<RoleRoute allowedRoles={["admin", "owner"]}><Page><OwnerUsers /></Page></RoleRoute>} />
          <Route path="announcements" element={<Page><AdminAnnouncements /></Page>} />
          <Route path="invites" element={<RoleRoute allowedRoles={["admin", "owner"]}><Page><AdminInvites /></Page></RoleRoute>} />
          <Route path="approvals" element={<RoleRoute allowedRoles={["admin", "owner"]}><Page><AdminApprovals /></Page></RoleRoute>} />
          <Route path="rooms" element={<Page><AdminRooms /></Page>} />
          <Route path="settings" element={<RoleRoute allowedRoles={["admin", "owner"]}><Page><AdminSettings /></Page></RoleRoute>} />
        </Route>

        <Route
          path="/staff/*"
          element={
            <ProtectedRoute>
              <RoleRoute allowedRoles={["staff"]}>
                <StaffLayout />
              </RoleRoute>
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="home" replace />} />
          <Route path="home" element={<Page><StaffHome /></Page>} />
          <Route path="transport" element={<Page><AdminTransport /></Page>} />
          <Route path="maintenance" element={<Page><StaffMaintenance /></Page>} />
          <Route path="inspections" element={<Page><StaffInspections /></Page>} />
          <Route path="expenses" element={<Page><StaffExpenses /></Page>} />
          <Route path="chat" element={<Page><AdminChat /></Page>} />
        </Route>

        <Route
          path="/student/*"
          element={
            <ProtectedRoute>
              <RoleRoute allowedRoles={["student"]}>
                <StudentLayout />
              </RoleRoute>
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="home" replace />} />
          <Route path="home" element={<Page><StudentHome /></Page>} />
          <Route path="transport" element={<Page><StudentTransport /></Page>} />
          <Route path="maintenance" element={<Page><StudentMaintenance /></Page>} />
          <Route path="chat" element={<Page><StudentChat /></Page>} />
          <Route path="contract" element={<Page><StudentContract /></Page>} />
          <Route path="profile" element={<Page><StudentProfile /></Page>} />
          <Route path="notifications" element={<Page><StudentNotifications /></Page>} />
          <Route path="move-out" element={<Page><StudentMoveOut /></Page>} />
          <Route path="documents" element={<Page><StudentDocuments /></Page>} />
        </Route>

        {/* Shared Help page — any authenticated user, no role gate */}
        <Route
          path="/help"
          element={
            <ProtectedRoute>
              <Page>
                <Help />
              </Page>
            </ProtectedRoute>
          }
        />

        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="*" element={<Page><NotFound /></Page>} />
      </Routes>
    </Suspense>
  );
}
